// =============================================================================
// MeuDriver — Batch P4: Hook de Log de Auditoria Admin
// =============================================================================

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuditLogEntry {
  id: string
  performed_by: string | null
  admin_name: string | null
  action: string
  target_type: string | null
  target_id: string | null
  details: Record<string, unknown>
  created_at: string
}

export interface AuditLogFilters {
  action?: string
  targetType?: string
  performedBy?: string
  from?: string
  to?: string
  page?: number
  pageSize?: number
}

export interface AuditLogResponse {
  rows: AuditLogEntry[]
  total: number
  page: number
  page_size: number
}

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const auditLogKeys = {
  all:  ['admin_audit_log'] as const,
  list: (filters: AuditLogFilters) => [...auditLogKeys.all, filters] as const,
}

// ---------------------------------------------------------------------------
// useAdminAuditLog
// ---------------------------------------------------------------------------

export function useAdminAuditLog(filters: AuditLogFilters = {}) {
  return useQuery<AuditLogResponse, Error>({
    queryKey: auditLogKeys.list(filters),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_admin_audit_log', {
        p_action:       filters.action       ?? null,
        p_target_type:  filters.targetType   ?? null,
        p_performed_by: filters.performedBy  ?? null,
        p_from:         filters.from         ?? null,
        p_to:           filters.to           ?? null,
        p_page:         filters.page         ?? 0,
        p_page_size:    filters.pageSize     ?? 50,
      })
      if (error) throw new Error(error.message)
      const result = data as AuditLogResponse
      return {
        rows:      result?.rows      ?? [],
        total:     result?.total     ?? 0,
        page:      result?.page      ?? 0,
        page_size: result?.page_size ?? 50,
      }
    },
    staleTime: 10_000,
  })
}
